import jwtDecode from 'jwt-decode';
import Overlay from '../../components/Overlay';
import { JwtClaims } from 'oidc-client-ts';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { setPageTitle, showSidebar } from '../../../Admin/adminLayoutSlice';
import ErrorPage from '../../components/ErrorPage';
import { getMyPMISiteUrl, getOidcToken, getPMISiteUrl } from '../../env-vars';
import {
  buildAdmin2faUrl,
  buildAdmin2faUrlWithAgreement,
  buildAdminInviteUrl,
} from '../../helpers';
import AuthService from '../../services/AuthService';
import { setCurrentAccount } from '../../store/shared/accountSlice';
import { userSelector } from '../../store/shared/userSlice';
import {
  useGetAdminAccountAccessQuery,
  useSetLastLoggedinMutation,
} from '../apiSlice';

interface IProps {
  render: () => React.ReactNode;
  pageName?: string;
}

export default function AdminPortalInitializer({
  render,
  pageName = undefined,
}: IProps) {
  const { accountId, dealId } = useParams<{
    accountId: string;
    dealId: string;
  }>();
  const [hasCompleted2Fa, setHasCompleted2Fa] = useState(false);
  const { value: user } = useSelector(userSelector);
  const { data, isLoading, isFetching, error } = useGetAdminAccountAccessQuery(
    accountId,
    { skip: !accountId || !user?.access_token },
  );
  const [setLastLoggedin] = useSetLastLoggedinMutation();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!isLoading && !isFetching && data && data.hasAccess) {
      const userSession = localStorage.getItem(getOidcToken());
      const parsedToken = userSession ? JSON.parse(userSession) : null;
      const storedJwt = jwtDecode<JwtClaims>(parsedToken?.access_token);

      if (!storedJwt?.rbac_2fa_raids) {
        if (dealId) {
          window.location.href = buildAdmin2faUrlWithAgreement(
            accountId,
            dealId,
            data.roleAssignmentId,
          );
        } else {
          window.location.href = buildAdmin2faUrl(
            accountId,
            data.roleAssignmentId,
          );
        }
      }

      const hasCompleted2FaForRole =
        storedJwt?.rbac_2fa_raids?.toString().includes(data.roleAssignmentId) ||
        false;
      // Only set last logged in and grant access if the user has completed 2FA for the role
      if (hasCompleted2FaForRole) {
        setLastLoggedin(accountId)
          .unwrap()
          .finally(() => {
            setHasCompleted2Fa(hasCompleted2FaForRole);
          });
      }

      dispatch(setCurrentAccount(accountId));
    } else if (
      !isLoading &&
      !isFetching &&
      data &&
      !data.hasAccess &&
      data.inviteId
    ) {
      AuthService.revokeUserTicket().then(() => {
        window.location.href = buildAdminInviteUrl(data.inviteId);
      });
    }
  }, [accountId, data, isFetching, isLoading, setLastLoggedin]);

  useEffect(() => {
    if (accountId && pageName) {
      dispatch(setPageTitle(pageName));
      dispatch(showSidebar(true));
    }
  }, [pageName]);

  if (error) {
    if ('status' in error && error.status === 404) {
      return (
        <ErrorPage status={404} message="Page Not Found">
          <h2>The page you are looking for cannot be found.</h2>
          <p>You may have followed a bad link or incorrectly typed the URL.</p>
          <p>
            Follow these popular links to find what you&apos;re looking for:{' '}
            <br /> <a href={getPMISiteUrl()}>Home</a> |{' '}
            <a href="/admin/select-account">Partner Platform</a> |{' '}
            <a
              href={getMyPMISiteUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              myPMI
            </a>
          </p>
        </ErrorPage>
      );
    }
    throw error;
  }

  if (isLoading || isFetching) {
    return (
      <Overlay
        variant="PMIOverlay"
        global
        animated
        fade
        iconColor="brand-dark"
        overlayColor="black"
        overlayOpacity={1}
        show={isLoading || isFetching}
      />
    );
  }

  if (!isLoading && !isFetching && data?.hasAccess && hasCompleted2Fa) {
    return <>{render()}</>;
  }

  if (!isLoading && !isFetching && data && !data.hasAccess && data.inviteId) {
    return (
      <div className="box">
        <h3 className="text-center">Redirecting to your account...</h3>
      </div>
    );
  }

  if (!isLoading && !isFetching && data && !data.hasAccess) {
    return (
      <div className="box">
        <h3 className="text-center">
          Your account does not have access to this partner&nbsp;account or have
          any pending&nbsp;invitations.
        </h3>
      </div>
    );
  }

  if (!isLoading && !isFetching && !data) {
    return (
      <ErrorPage status={404} message="Page Not Found">
        <h2>The page you are looking for cannot be found.</h2>
        <p>You may have followed a bad link or incorrectly typed the URL.</p>
        <p>
          Follow these popular links to find what you&apos;re looking for:{' '}
          <br /> <a href={getPMISiteUrl()}>Home</a> |{' '}
          <a href="/admin/select-account">Partner Platform</a> |{' '}
          <a href={getMyPMISiteUrl()} target="_blank" rel="noopener noreferrer">
            myPMI
          </a>
        </p>
      </ErrorPage>
    );
  }

  return null;
}
